import React, { FC, useEffect } from "react";

import { AttachmentActions, Attachments, BaseComponentProps } from "@keepeek/refront-components";
import { Box, LinearProgress } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";
import clsx from "clsx";

import { KIcon } from "../../../components/common/KIcon";
import { useAttachments } from "../../../providers/attachment/hooks/attachments";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetWithElementProps } from "../models";

export type KAttachmentsProps = {
  widgetId: string;
} & KWidgetWithElementProps &
  BaseComponentProps &
  KWidgetWithElementProps;

const KAttachments: FC<React.PropsWithChildren<KAttachmentsProps>> = function ({
  elementId,
  className,
  testId = "KAttachments",
  type,
}) {
  const attachmentWidgetId = "PJ"; // not used to find state in atom
  const { setWidgetState, widgetState } = useWidgetState({
    id: attachmentWidgetId,
    type,
  });
  const { attachments, loadingAttachments, onDownloadAttachment, copyAttachment } = useAttachments({
    elementId: elementId || undefined,
  });

  useEffect(() => {
    if (loadingAttachments) {
      setWidgetState({ state: WidgetUIState.LOADING });
    } else if (attachments && attachments.length > 0) {
      setWidgetState({ state: WidgetUIState.RENDERED, elementCount: attachments.length });
    } else {
      setWidgetState({ state: WidgetUIState.NULL });
    }
  }, [attachments, loadingAttachments, setWidgetState]);

  const rootStyles: SxProps<Theme> = {
    textAlign: "center",
    marginTop: 2,
    marginBottom: 1,
    flex: 1,
  };

  switch (widgetState.state) {
    case WidgetUIState.LOADING:
      return (
        <Box
          className={clsx("KAttachments", "KAttachements-loading", className)}
          sx={rootStyles}
          data-testid={testId}
        >
          <LinearProgress />
        </Box>
      );
    case WidgetUIState.RENDERED:
      return (
        <Box className={clsx("KAttachments", className)} data-testid={testId} sx={rootStyles}>
          <Attachments
            attachments={attachments || []}
            onAttachmentDownload={onDownloadAttachment}
            onAttachmentCopy={copyAttachment}
            attachmentActionAvailable={[AttachmentActions.Copy, AttachmentActions.Download]}
            sx={{
              padding: 1,
            }}
            KIcon={KIcon}
          />
        </Box>
      );
    default:
      return null;
  }
};
export default KAttachments;
